import { ID } from 'types';

/**
 * Which line item, if any, has its "Why this price?" drawer open.
 *
 * Held in a module-level store rather than state in the cell renderer, which Handsontable mounts
 * once per visible cell — 100+ at a time on this page. Owning `open` there meant 100+ mounted
 * drawers and 100+ live explanation queries, each one state flip away from firing a whole-event
 * request. It cannot live in LineItemsProvider's reducer either: that dispatches on every
 * keystroke, and this directory's CLAUDE.md keeps UI-only toggles out of it.
 *
 * Reads go through `useSyncExternalStore`, so only the subscribed drawer host re-renders on open —
 * the grid never does. `getSelection` returns the same reference until the selection actually
 * changes, which is what keeps that hook from looping.
 */
export interface SuggestedPriceDrawerSelection {
  lineItemId: ID;
  lotId: ID;
}

let selection: SuggestedPriceDrawerSelection | null = null;
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach(listener => listener());
};

const isSameSelection = (
  a: SuggestedPriceDrawerSelection | null,
  b: SuggestedPriceDrawerSelection | null,
): boolean => (
  String(a?.lineItemId) === String(b?.lineItemId) && String(a?.lotId) === String(b?.lotId)
);

export const suggestedPriceDrawerStore = {
  close: (): void => {
    if (selection === null) return;
    selection = null;
    notify();
  },
  getSelection: (): SuggestedPriceDrawerSelection | null => selection,
  open: (next: SuggestedPriceDrawerSelection): void => {
    if (isSameSelection(selection, next)) return;
    selection = next;
    notify();
  },
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
};
